import { MethodCardProps } from './props';
import styles from './styles.module.scss';
import { Image, Input, ToolTip } from '@starsoft/common/components';
import { memo } from 'react';
import MethodCardSkeleton from './Skeleton';
import {
  useCurrentModal,
  useSearchParams,
  useTranslation,
} from '@starsoft/common/hooks';
import { CommonModalsKey, WithdrawMethodId } from '../../../../../../enums';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * MethodCard component displays a card for a transaction method.
 *
 * @param {MethodCardProps} props - The properties for the MethodCard component.
 * @param {object} props.method - The method object containing details about the transaction method.
 * @param {function} props.onClick - The function to call when the card is clicked.
 * @param {boolean} props.isLoading - A flag indicating if the data is still loading.
 *
 * @returns {JSX.Element} A JSX element representing the method card.
 *
 * Example usage:
 * <MethodCard method={method} onClick={handleClick} isLoading={false} />
 */
function MethodCard({ method, onClick, isLoading }: MethodCardProps) {
  const { t: tCommon } = useTranslation('common');
  const search: URLSearchParams = useSearchParams();
  const methodId = search.get('method-id');
  const modal = useCurrentModal(CommonModalsKey);

  // Determine if the current method is selected
  const isChecked: boolean = methodId === method?.id?.toString();
  // Check if the method is a crypto transaction method
  const isCryptoTransactionMethod: boolean =
    method?.id?.toString() === WithdrawMethodId.Crypto?.toString();

  /**
   * Get the display name for the method.
   *
   * @returns {string} The name of the method.
   *
   * Example:
   * getName() // returns 'Crypto Coin' if method id is for crypto
   */
  function getName(): string {
    switch (method?.id?.toString()) {
      case WithdrawMethodId.Crypto?.toString():
        return tCommon('crypto_coin');
    }

    return method?.name;
  }

  /**
   * Get the description for the method.
   *
   * @returns {string} The description of the method.
   *
   * Example:
   * getDescription() // returns 'Fast Verification' if method id is for crypto
   */
  function getDescription(): string {
    switch (method?.id?.toString()) {
      case WithdrawMethodId.Crypto?.toString():
        return tCommon('fast_verification_label');
      case WithdrawMethodId.BetterbroBrazillianPix?.toString():
        return tCommon('pix_timing_label');
    }

    return method?.name;
  }

  // If the data is still loading, show a skeleton loader
  if (isLoading) {
    return <MethodCardSkeleton />;
  }

  // Render the method card
  return (
    <div
      className={`${styles.container} ${isChecked ? styles['container--checked'] : ''}`}
      onClick={() => onClick(method)}
    >
      <div className={styles.container__row}>
        <Image
          className={styles.container__image}
          format="square"
          width={32}
          height={32}
          alt="icon-square"
          src={
            method?.id?.toString() ===
              WithdrawMethodId.BetterbroIndianImps?.toString() &&
            modal === CommonModalsKey.Withdraw
              ? `${SettingsService.env.S3_BUCKET_BASE_URL}/icons/imps.png`
              : `${SettingsService.env.S3_BUCKET_BASE_URL}/${
                  isCryptoTransactionMethod ? 'coins/rounded' : 'icons'
                }/${isCryptoTransactionMethod ? 'bitcoin.svg' : method?.image}`
          }
          secondarySkeleton
        />
        <div className={styles.container__column}>
          <div className={styles.container__row}>
            <span className={styles.container__name}>
              {getName()}
              {method?.gateway?.image && (
                <ToolTip titleComponent={method?.gateway?.name}>
                  <div>
                    <Image
                      width={80}
                      height={12}
                      alt={`method-gateway-icon-${method?.gateway?.id}`}
                      className={styles.container__gateway__image}
                      src={`${SettingsService.env.S3_BUCKET_BASE_URL}/icons/${method?.gateway?.image}`}
                    />
                  </div>
                </ToolTip>
              )}
            </span>
            {/* @ts-ignore */}
            {getMoneyOrAmount(method?.fee) === 0 && (
              <div className={styles.container__chip}>
                {tCommon('zero_fee')}
              </div>
            )}
          </div>
          <span
            className={`${styles.container__description} ${
              method?.id?.toString() === WithdrawMethodId.Crypto?.toString()
                ? styles['container__description--primary']
                : ''
            }`}
          >
            {getDescription()}
          </span>
        </div>
      </div>
      <Input type="radio" checked={isChecked} readOnly />
    </div>
  );
}

export default memo(MethodCard);
